// src/components/useCurrentUser.js
import { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";

/**
 * Hook personalizado para obtener el usuario actual de Firebase Auth
 * @returns {Object} { user, loading, error }
 * - user: El objeto de usuario de Firebase o null si no está autenticado
 * - loading: true mientras se verifica el estado de autenticación
 * - error: Mensaje de error si algo falla
 */
export function useCurrentUser() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    
    // Suscribirse a cambios en el estado de autenticación
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        setUser(user);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error("Auth state change error:", error);
        setError(error.message);
        setUser(null);
        setLoading(false);
      }
    );

    // Cleanup al desmontar el componente
    return () => unsubscribe();
  }, []);

  return { user, loading, error };
}

/**
 * Hook para obtener el ID token del usuario actual
 * @returns {Object} { getToken, tokenError }
 * - getToken: Función async que retorna el ID token
 * - tokenError: Estado de error al obtener el token
 */
export function useIdToken() {
  const [tokenError, setTokenError] = useState(null);
  const auth = getAuth();

  const getToken = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("No authenticated user");
      }
      const token = await user.getIdToken();
      setTokenError(null);
      return token;
    } catch (error) {
      console.error("Error getting ID token:", error);
      setTokenError(error.message);
      throw error;
    }
  };

  return { getToken, tokenError };
}

/**
 * Hook combinado para usar en componentes que requieren autenticación
 * @returns {Object} Todos los valores y funciones útiles
 */
export default function useAuth() {
  const { user, loading, error } = useCurrentUser();
  const { getToken, tokenError } = useIdToken();

  return {
    // Estado del usuario
    user,
    loading,
    error: error || tokenError,
    
    // Propiedades útiles
    isAuthenticated: !!user,
    uid: user?.uid || null,
    email: user?.email || null,
    displayName: user?.displayName || null,
    photoURL: user?.photoURL || null,
    
    // Funciones
    getToken,
    
    // Métodos de Firebase Auth expuestos
    signOut: async () => {
      const auth = getAuth();
      await auth.signOut();
    }
  };
}